import { z as z$1 } from "zod";

// src/models/vendors/bridge/patientResponsibility.ts
const bridgePatientResponsibilityDeductibleTypeSchema = z$1.enum(["individual", "family"]);
/** Bridge patient-responsibility component breakdown (USD cents). */
const bridgePatientResponsibilityBreakdownSchema = z$1.object({
	total: z$1.number().int().nonnegative(),
	copayment: z$1.number().int().nonnegative().nullish(),
	deductible: z$1.number().int().nonnegative().nullish(),
	coinsurance: z$1.number().int().nonnegative().nullish(),
	coinsurancePercent: z$1.number().int().nonnegative().nullish(),
	fallback: z$1.number().int().nonnegative().nullish(),
	oopFallback: z$1.number().int().nonnegative().nullish(),
	oopCopayment: z$1.number().int().nonnegative().nullish(),
	oopCoinsurance: z$1.number().int().nonnegative().nullish(),
	oopDeductible: z$1.number().int().nonnegative().nullish(),
	deductibleType: bridgePatientResponsibilityDeductibleTypeSchema.nullish()
});
/** Bridge returns patientResponsibility as USD cents or a breakdown object with `total`. */
function parseBridgePatientResponsibilityTotal(value) {
	if (value == null) return;
	if (typeof value === "number") return Number.isInteger(value) && value >= 0 ? value : void 0;
	if (typeof value === "object") {
		const total = value.total;
		if (typeof total === "number" && Number.isInteger(total) && total >= 0) return total;
	}
}
/** Bridge often sends `null` for optional enums (e.g. `deductibleType`). Zod `.optional()` rejects null and would drop line items. */
function omitNullBreakdownFields(value) {
	return Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue != null));
}
function parseBridgePatientResponsibilityBreakdown(value) {
	if (value == null) return;
	if (typeof value === "number") {
		const total = parseBridgePatientResponsibilityTotal(value);
		return total != null ? { total } : void 0;
	}
	if (typeof value === "object") {
		const total = parseBridgePatientResponsibilityTotal(value);
		if (total == null) return;
		const parsed = bridgePatientResponsibilityBreakdownSchema.safeParse({
			...omitNullBreakdownFields(value),
			total
		});
		return parsed.success ? parsed.data : { total };
	}
}
function parseBridgePatientResponsibilityFields(value) {
	const breakdown = parseBridgePatientResponsibilityBreakdown(value);
	if (breakdown) return {
		total: breakdown.total,
		breakdown
	};
	const total = parseBridgePatientResponsibilityTotal(value);
	return total != null ? { total } : {};
}
function buildBridgeServiceEligibilityPatientResponsibilityWriteFields(rawPatientResponsibility) {
	const fields = parseBridgePatientResponsibilityFields(rawPatientResponsibility);
	return {
		...fields.total != null ? { patientResponsibility: fields.total } : {},
		...fields.breakdown ? { patientResponsibilityBreakdown: fields.breakdown } : {}
	};
}
function parseNonNegativeIntCents(value) {
	return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : void 0;
}
/** Pre-bill Bridge `patientResponsibility.status`. Not a claims-final total. */
function isBridgePatientResponsibilityEstimateStatus(status) {
	return typeof status === "string" && status.trim().toUpperCase() === "ESTIMATE";
}
/** Parse Bridge Service snapshot `patientResponsibility.amount` (total, paid, components). */
function parseBridgeServicePatientResponsibilityFromSnapshot(snapshot) {
	if (!snapshot) return {};
	const patientResponsibility = snapshot.patientResponsibility;
	if (patientResponsibility == null) return {};
	if (typeof patientResponsibility === "object") {
		const responsibility = patientResponsibility;
		if (isBridgePatientResponsibilityEstimateStatus(responsibility.status)) return {};
		const amount = responsibility.amount;
		if (amount != null && typeof amount === "object") {
			const amountRecord = amount;
			const total = parseBridgePatientResponsibilityTotal(amountRecord);
			const paid = parseNonNegativeIntCents(amountRecord.paid) ?? parseNonNegativeIntCents(responsibility.paid);
			const breakdown = parseBridgePatientResponsibilityBreakdown(amountRecord);
			const outstandingCents = total != null && paid != null ? Math.max(0, total - paid) : total;
			return {
				...total != null ? { finalPatientResponsibility: total } : {},
				...breakdown ? { finalPatientResponsibilityBreakdown: breakdown } : {},
				...paid != null ? { finalPaidCents: paid } : {},
				...outstandingCents != null ? { outstandingCents } : {}
			};
		}
		const fields = parseBridgePatientResponsibilityFields(patientResponsibility);
		return {
			...fields.total != null ? {
				finalPatientResponsibility: fields.total,
				outstandingCents: fields.total
			} : {},
			...fields.breakdown ? { finalPatientResponsibilityBreakdown: fields.breakdown } : {}
		};
	}
	const total = parseBridgePatientResponsibilityTotal(patientResponsibility);
	return total != null ? {
		finalPatientResponsibility: total,
		outstandingCents: total
	} : {};
}

export { bridgePatientResponsibilityBreakdownSchema, bridgePatientResponsibilityDeductibleTypeSchema, buildBridgeServiceEligibilityPatientResponsibilityWriteFields, parseBridgePatientResponsibilityBreakdown, parseBridgePatientResponsibilityFields, parseBridgePatientResponsibilityTotal, parseBridgeServicePatientResponsibilityFromSnapshot };
//# sourceMappingURL=patientResponsibility.mjs.map